import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi, clearAdminToken } from '../../services/adminApi';
import { Loader2, ArrowLeft, KeyRound, Copy, RefreshCcw, Ban, Plus } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (v) => (v ? new Date(v).toLocaleString() : '—');

const keyState = (k) => {
  if (!k.is_active) return { label: 'Revoked', cls: 'text-muted-foreground border-border' };
  if (k.revoke_at) return { label: `Ends ${new Date(k.revoke_at).toLocaleDateString()}`, cls: 'text-amber-400 border-amber-500/30 bg-amber-500/10' };
  return { label: 'Active', cls: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10' };
};

const NewKeyBanner = ({ issued, onClose }) => (
  <div className="rounded-md border border-primary/40 bg-primary/10 p-4 space-y-2" data-testid="ai-key-issued-banner">
    <p className="text-sm font-semibold">New EQUIPR AI key for {issued.tenantName}. Copy it now: it won't be shown again.</p>
    <div className="flex gap-2">
      <input readOnly value={issued.api_key} className="flex-1 h-9 px-3 rounded-md bg-card border border-border font-mono text-sm" data-testid="ai-key-issued-value" />
      <button onClick={() => { navigator.clipboard.writeText(issued.api_key); toast.success('Key copied'); }} className="h-9 px-3 rounded-md border border-border text-sm flex items-center gap-1.5 hover:bg-secondary" data-testid="ai-key-copy-btn"><Copy className="w-4 h-4" />Copy</button>
      <button onClick={onClose} className="h-9 px-3 rounded-md text-sm text-muted-foreground hover:text-foreground" data-testid="ai-key-banner-close">Done</button>
    </div>
    {issued.rotated_from && <p className="text-xs text-muted-foreground">The old key keeps working for the overlap period, then stops automatically.</p>}
  </div>
);

const KeyRow = ({ k, tenantName, busy, onRotate, onRevoke }) => {
  const s = keyState(k);
  return (
    <tr className="border-t border-border" data-testid={`ai-key-row-${k.id}`}>
      <td className="p-3">{tenantName}</td>
      <td className="p-3 font-mono text-xs">{k.key_prefix}…</td>
      <td className="p-3"><span className={`text-[10px] font-mono uppercase tracking-widest border rounded-sm px-1.5 py-0.5 ${s.cls}`}>{s.label}</span></td>
      <td className="p-3 font-mono text-xs">{fmt(k.created_at)}<div className="text-muted-foreground">{k.created_by}</div></td>
      <td className="p-3 font-mono text-xs">{fmt(k.last_used_at)}</td>
      <td className="p-3 font-mono text-xs">{fmt(k.expires_at)}</td>
      <td className="p-3 text-right whitespace-nowrap">
        {k.is_active && !k.revoke_at && (
          <button disabled={busy} onClick={() => onRotate(k)} className="text-xs px-2.5 py-1.5 rounded-sm border border-border hover:bg-secondary mr-2 inline-flex items-center gap-1" data-testid={`ai-key-rotate-${k.id}`}><RefreshCcw className="w-3.5 h-3.5" />Rotate</button>
        )}
        {k.is_active && (
          <button disabled={busy} onClick={() => onRevoke(k)} className="text-xs px-2.5 py-1.5 rounded-sm border border-red-500/40 text-red-400 hover:bg-red-500/10 inline-flex items-center gap-1" data-testid={`ai-key-revoke-${k.id}`}><Ban className="w-3.5 h-3.5" />Revoke</button>
        )}
      </td>
    </tr>
  );
};

const AdminAiKeysPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ keys: [], tenants: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [tenantId, setTenantId] = useState('');
  const [overlap, setOverlap] = useState(7);
  const [issued, setIssued] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData((await adminApi.aiKeys()).data); }
    catch (e) { if (e.response?.status === 401) { clearAdminToken(); navigate('/admin/login', { replace: true }); } }
    finally { setLoading(false); }
  }, [navigate]);
  useEffect(() => { load(); }, [load]);

  const names = Object.fromEntries(data.tenants.map(t => [t.id, `${t.name} (${t.client_id || '—'})`]));
  const selected = data.tenants.find(t => t.id === tenantId);
  const activeForSelected = data.keys.filter(k => k.tenant_id === tenantId && k.is_active);

  const act = async (fn) => {
    setBusy(true);
    try { await fn(); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Request failed'); }
    finally { setBusy(false); }
  };
  const issue = (body) => act(async () => {
    const r = await adminApi.issueAiKey(body);
    setIssued({ ...r.data, tenantName: names[body.tenant_id] });
  });
  const onRotate = (k) => {
    if (window.confirm(`Rotate ${k.key_prefix}…? A new key is issued now and the old one stops after ${overlap} day(s).`)) {
      issue({ tenant_id: k.tenant_id, rotate_from: k.id, overlap_days: overlap });
    }
  };
  const onRevoke = (k) => {
    if (window.confirm(`Revoke ${k.key_prefix}… now? Anything using it stops working immediately.`)) {
      act(async () => { await adminApi.revokeAiKey(k.id); toast.success('Key revoked'); });
    }
  };
  const shown = tenantId ? data.keys.filter(k => k.tenant_id === tenantId) : data.keys;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-ai-keys-page">
      <header className="border-b border-border bg-card/40 sticky top-0 z-10 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <button onClick={() => navigate('/admin/tenants')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="admin-back-to-tenants"><ArrowLeft className="w-4 h-4" /> Tenants</button>
          <h1 className="text-base font-bold uppercase tracking-tight flex items-center gap-2"><KeyRound className="w-4 h-4 text-primary" />EQUIPR AI keys</h1>
          <div />
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <p className="text-sm text-muted-foreground max-w-3xl">Read-only keys for the EQUIPR AI endpoints. One active key per company. Rotating issues a new key and ends the old one after the overlap period. Company API keys made in Settings don't work here.</p>
        {issued && <NewKeyBanner issued={issued} onClose={() => setIssued(null)} />}
        <div className="flex flex-wrap items-end gap-3">
          <select value={tenantId} onChange={(e) => setTenantId(e.target.value)} className="h-9 px-3 rounded-md bg-card border border-border text-sm min-w-[16rem]" data-testid="ai-key-tenant-select">
            <option value="">All companies</option>
            {data.tenants.map(t => <option key={t.id} value={t.id}>{t.name} ({t.client_id || '—'}){t.is_demo ? ' · demo' : ''}</option>)}
          </select>
          <label className="text-xs text-muted-foreground flex items-center gap-2">Rotation overlap (days)
            <input type="number" min={1} max={30} value={overlap} onChange={(e) => setOverlap(Math.min(30, Math.max(1, parseInt(e.target.value) || 7)))} className="w-16 h-9 px-2 rounded-md bg-card border border-border text-sm" data-testid="ai-key-overlap-input" />
          </label>
          {selected && activeForSelected.length === 0 && (
            <button disabled={busy || !selected.timezone_ready} onClick={() => issue({ tenant_id: tenantId })} className="h-9 px-3 rounded-md bg-primary text-primary-foreground text-sm inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="ai-key-issue-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}Issue key
            </button>
          )}
          {selected && !selected.timezone_ready && <span className="text-xs text-amber-400" data-testid="ai-key-tz-warning">Set and confirm this company's time zone first.</span>}
        </div>
        <div className="rounded-md border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-xs uppercase tracking-widest text-muted-foreground">
              <tr><th className="text-left p-3">Company</th><th className="text-left p-3">Key</th><th className="text-left p-3">Status</th><th className="text-left p-3">Issued</th><th className="text-left p-3">Last used</th><th className="text-left p-3">Expires</th><th className="p-3" /></tr>
            </thead>
            <tbody>
              {shown.map(k => <KeyRow key={k.id} k={k} tenantName={names[k.tenant_id] || k.tenant_id} busy={busy} onRotate={onRotate} onRevoke={onRevoke} />)}
              {!loading && shown.length === 0 && <tr><td colSpan={7} className="p-12 text-center text-muted-foreground" data-testid="ai-key-empty">No EQUIPR AI keys{selected ? ' for this company' : ''} yet.</td></tr>}
            </tbody>
          </table>
        </div>
        {loading && <div className="flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>}
      </main>
    </div>
  );
};

export default AdminAiKeysPage;
